/**
 * Folding the pages of a meeting history into one list, and knowing when paging has stopped
 * getting anywhere.
 *
 * Kept as a pure function with no imports of its own, so it can be tested directly — the rule the
 * package's CLAUDE.md gives for logic that would otherwise only be reachable through a hook.
 *
 * **Both halves exist because the store's cursor is a bare millisecond timestamp.** `recent()` filters
 * `recordedAt.lessThan(cursor)`, so meetings recorded in the same millisecond straddle a page boundary
 * and the ones past the edge are stepped over. The caller asks for `cursor + 1` instead, which means
 * `<= cursor` and deliberately hands the boundary row back again — hence the dedupe.
 *
 * That repair has its own edge, one page wide: if MORE meetings share a millisecond than fit in a
 * page, every later request returns the same full page, the cursor comes back unchanged, and the
 * reader presses "Show earlier meetings" forever. `stalled` is how that terminates. It costs the
 * meetings older than the tie, which nothing with a timestamp-only cursor could have reached anyway —
 * the real fix is a composite cursor (`recordedAt` + `meetingSessionId`) in the store, worth doing if
 * write-ups ever land in bursts. Today a tie needs two meetings to END in the same millisecond, and
 * this needs eleven.
 */

/** The one field the fold needs. Structural on purpose, so this file imports nothing. */
interface Identified {
  meetingSessionId: string;
}

export interface FoldedMeetingPages<T> {
  meetings: T[];
  /** The last page returned rows and every one was already shown — asking again cannot help. */
  stalled: boolean;
}

export function foldMeetingPages<T extends Identified>(pages: readonly { meetings: T[] }[]): FoldedMeetingPages<T> {
  const seen = new Set<string>();
  const meetings: T[] = [];
  let stalled = false;

  for (const page of pages) {
    const had = meetings.length;
    for (const meeting of page.meetings) {
      if (seen.has(meeting.meetingSessionId)) continue;
      seen.add(meeting.meetingSessionId);
      meetings.push(meeting);
    }
    // An EMPTY page is the end of the history, not a stall — the cursor stops coming with it.
    stalled = page.meetings.length > 0 && meetings.length === had;
  }

  return { meetings, stalled };
}
